import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Code } from 'constants/codes';
import { Utility } from 'helpers/UtilityHelpers';
import { MessageType, Privilege } from 'models/BaseModel'
import { useNavigate } from "react-router-dom";
import { RefObject } from "react";
import { downloadPdf } from "components/common/SaveToPdf"; 
import { API } from "helpers/ApiHelpers";
import { Client } from "models/ClientModel";
import { Emailer } from "helpers/EmailerHelipers";
import { Field } from 'components/common/FieldManager';

interface LinkButtonProps {
    id: string;
    title: string;
    info?: string;
    className?: string;
    onClick: () => void; // Now `onClick` is mandatory
}
  
const LinkButton: React.FC<LinkButtonProps> = ({
    id,
    info = '',
    className = '',
    title,
    onClick,
  }) => {
    return (
      <button type="button" id={id} onClick={onClick} className={`tbutton ${className}`} title={info}>
        {title}
      </button>
    );
};

interface ActionButtonProps {
  title: string;
  info?: string;
  className?: string;
  animation?: boolean;
  color?: number;
  visible?: boolean;
  enable?: boolean;
  onClick: () => Promise<void> | void;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  title,
  info = '',
  className = '',
  animation = true,
  color = 0,
  visible = true,
  enable = true,
  onClick,
}) => {

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if(animation) {
      const button = e.currentTarget;
      button.classList.add("spintext");

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await onClick(); // support both sync and async
      } finally {
        button.classList.remove("spintext");
      }
    } else { onClick(); }
  };

  if(!visible) return null;
    
  return (
   enable ?
      <button 
        type="button" 
        onClick={handleClick} 
        className={`tbutton bcolor${color} ${className? className : ''}`} 
        title={info}>
        {title}
      </button>
      :
      <button 
        type="button" 
        className={`tbutton disabled ${className? className : ''}`} 
        title={'No privileges to execute this action.'}
        disabled={true}>
        {title}
      </button>
  );
};

interface ActionDisableProps {
  title: string;
  info?: string;
  className?: string;
  animation?: boolean;
  color?: number;
  visible?: boolean;
  enable?: boolean;
  onClick: () => Promise<void> | void;
}

const ActionDisable: React.FC<ActionDisableProps> = ({
  title,
  info = '',
  className = '',
  animation = true,
  color = 0,
  visible = true,
  enable = true,
  onClick,
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsDisabled(true); 
    if(animation) {
      const button = e.currentTarget;
      button.classList.add("spintext");

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await onClick(); // support both sync and async
      } finally {
        button.classList.remove("spintext");
      }
    } 
    else { onClick(); }
  };

  if(!visible) return null;
    
  return (
   enable ?
      <button 
        type="button" 
        onClick={handleClick} 
        className={`tbutton bcolor${color} ${className? className : ''}`} 
        title={info}
        disabled={isDisabled}
      >
        {title}
      </button>
      :
      <button 
        type="button" 
        className={`tbutton disabled ${className? className : ''}`} 
        title={'No privileges to execute this action.'}
        disabled={true}>
        {title}
      </button>
  );
};

interface AutoGenerateButtonProps {
  keyId?: string;
  title: string;
  amount?: number;
  IdValue?: { id: number, status: number };
  isOrderGenerated?: boolean;
  onGenerate: () => Promise<void> | void;
  onView: () => Promise<void> | void;
}

const AutoGenerateButton: React.FC<AutoGenerateButtonProps> = ({
  title,
  amount = '',
  IdValue = { id: 2, status: 2},
  isOrderGenerated = false,
  onGenerate,
  onView,
}) => {
  if(isOrderGenerated){
    IdValue  = IdValue ?? { id: 2, status: 2};
    const isGenerate = IdValue.id > 2;
    if(isGenerate){
      return (
        <Button.Action 
          title={title} 
          info={`View ${title}: $${amount}`} 
          onClick={onView} 
          color={0} 
          animation={false} 
        />
      )
    }
    else
    {
      let info = `Pending: $${amount} `;
      if(title==="Payment") 
        info += `(Payment can be added directly from the invoice only)`;
      else 
        info += `(Click here to generate ${title.toLocaleLowerCase()})`; 
      
      return (
        <Button.Disable 
          title={title} 
          info={info} 
          onClick={onGenerate} 
          color={1} 
          animation={false} 
        />
      )
    }
  }
  else{
    return (
        <Button.Action 
          title={title} 
          info={`Disabled : Generate Order first to enable this button - ${title}: $${amount} pending`} 
          onClick={() =>(console.log("This option is disabled"))} 
          color={12} 
          animation={false}
        />
      )
  }
};

interface IconButtonProps {
  info?: string;
  animation?: boolean;
  className?: string;
  onClick: () => Promise<void> | void;
}

const IconButton: React.FC<IconButtonProps> = ({
  info = '',
  className = '',
  animation = true,
  onClick,
}) => {

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if(animation)
    {
      const button = e.currentTarget;
      button.classList.add('spinner');

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await onClick(); 
      } finally {
        button.classList.remove('spinner');
      }
    }
    else { onClick(); }
    
  };

  return (
    <button 
      type="button" 
      onClick={handleClick} 
      className={`ibutton mr2 ${className? className : ''}`} 
      title={info}>
      &nbsp;
    </button>
  );
};

interface FrameActionButtonProps {
  keyId?: string;
  title: string;
  info?: string;
  infoDisable?: string;
  size?: [number,number,number,string?] | null | undefined;
  className?: string;
  classButton?: string;
  disable?: boolean;
  visible?: boolean;
  alert?: boolean;
  onClick: () => void; 
  align?: typeof Code.Align[keyof typeof Code.Align];
}

const FrameActionButton: React.FC<FrameActionButtonProps> = ({
  keyId = '',
  title,
  info = '',
  infoDisable = '',
  size,
  className = 'tbutton',
  classButton = '',
  disable = false,
  visible = true,
  alert = false,
  onClick,
  align = Code.Align.LEFT,
}) => {
  let frameClass = Utility.Get.alignClass(align);
  
  classButton += title == '+' ? ' pbutton' : ''
  
  if (keyId === Code.Key.NEW) return null;

  if(disable){
    info = infoDisable ? `(Disabled) ${infoDisable}` : `(Disabled) ${info}`;
  }

  return (
    visible ? 
      <div className={`fbutton ${frameClass} ${className} ${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
        <button type="button" onClick={onClick} className={`tbutton ${classButton? classButton : ''} ${disable ? 'bdisable':''} ${alert ? 'bcolor1':''}`} disabled={disable} title={info}>
          {title}
        </button>
      </div> : null
  );
};

interface CreateButtonProps {
  keyId?: string;
  title: string;
  info?: string;
  className?: string;
  Privilege: Privilege;
  onClick: () => void; 
}

const CreateButton: React.FC<CreateButtonProps> = ({
  keyId = '',
  title,
  info,
  className = '',
  Privilege,
  onClick,
}) => {

  title = Utility.Get.FormFullName(title);

  const formattedTitle = Utility.Convert.TitleCase(title?.toString() || '');

  if (keyId === Code.Key.NEW) return null; 

  return (
    <>
      { (Privilege.ALL || Privilege.CREATE) ? (
        <button 
          type="button" 
          onClick={onClick} 
          className={`tbutton ${className}`} 
          title={info ? info : `Create ${formattedTitle.toLowerCase()}`}
        >
          +&nbsp; {formattedTitle}
        </button>
      ) : (
        <button 
          type="button" 
          className={`tbutton disabled ${className} `} 
          title={`No privilege to create new ${formattedTitle.toLowerCase()}`} 
          disabled
        >
          +&nbsp; {formattedTitle}
        </button>
      )}
    </>
  );
};

interface DeleteButtonProps {
  onClick: () => void;
  activeId?: number;
  className?: string;
  Privilege: Privilege,
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  onClick,
  activeId = 2,
  Privilege,
}) => {
  const title = activeId === 2 ? "Delete" : "Restore";

  return (
    Privilege.ALL ||  Privilege.DELETE ? 
    <button 
      type="button" 
      id="btnDelete" 
      className={`ibutton ${activeId === 2 ? "bdelete" : "brestore"}`}  
      onClick={onClick} 
      title={`${title} selected records`}>&nbsp;</button>
    :
    <button 
      type="button" 
      id="btnDelete" 
      className={`ibutton disabled ${activeId === 2 ? "bdelete" : "brestore"}`} 
      title={`No privileges to ${title.toLowerCase()} records.`} 
      disabled={true}>&nbsp;</button>
  );
};

interface TopButtonsProps {
  formName?: string;
  children?: React.ReactNode;
}

const TopButtons: React.FC<TopButtonsProps> = ({ 
  formName='',
  children
}) => {
  const navigate = useNavigate();
  return (
    <div className="form-buttons">
      <div 
        className="print-head">
          {formName.toUpperCase()}
      </div>

      {children}

    </div>
)};

interface SaveButtonsProps {
  keyId: string;
  activeId: number | undefined;
  hasUnsaved?: boolean;
  title?: string;
  formName?: string;
  formId?: number | undefined;
  clientNumber?: string;
  userName?: string;
  Privilege: Privilege,
  print?: boolean,
  handleDelete: () => void;
  className?: string;
  children?: React.ReactNode;
  companyId?: Number;
}

const SaveButtons: React.FC<SaveButtonsProps> = ({ 
  keyId, 
  activeId, 
  hasUnsaved = true,
  title='',
  formName='',
  formId = 0,
  clientNumber,
  userName,
  Privilege,
  print = false,
  handleDelete,
  className='',
  children,
  companyId = 8
}) => {
  const navigate = useNavigate();
  const formFullname = Utility.Get.FormFullName(formName);
  const notCommon = activeId != Code.Option.Active.COMMON || companyId == 8 || companyId == 10;

  return (
    <div className={`form-buttons lcolor${formName} ${className}`}>
      {
        notCommon && (Privilege.ALL ||  Privilege.CREATE || Privilege.MODIFY) ?
          <button 
            id='btnSave' 
            className={`tbutton ${hasUnsaved ? '' : 'bdisable'}`} 
            type='submit' 
            accessKey='s'
            disabled={!hasUnsaved}
            title={hasUnsaved ? `${keyId === Code.Key.NEW ? 'Create new record (Alt+S)' : 'Update this record (Alt+S)'}` : "You haven't changed anything yet."}
            >
            {keyId === Code.Key.NEW ? 'Save' : 'Save'}
          </button>
          :
          <button 
            id='btnSave' 
            className='tbutton disabled' 
            title={`${keyId === Code.Key.NEW ? 'This record is protected or No privileges to create new record.' : 'This record is protected or No privileges to update this record.'}`}
            disabled={true}
            >
            {keyId === Code.Key.NEW ? 'Create' : 'Update'}
          </button>
      }

      {keyId !== Code.Key.NEW && print && (
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id='btnPrint'
          className='ibutton bprint' 
          title={`Print this ${formFullname} (Alt+P)`} 
          type='button' 
          accessKey='p'
          onClick={() => navigate(`/${formName}/print/${keyId}`)}
          >
        </button>
        :
        <button 
          id='btnPrint'
          className='btnPrint bprint disabled'
          title={`No privileges to print this ${formFullname}`} 
          disabled={true}
          >
        </button>
      )}
      
      {keyId !== Code.Key.NEW && (
        notCommon && (Privilege.ALL ||  Privilege.DELETE) ?
        <button 
          id='btnDelete'
          className={`ibutton ${activeId === 2 ? 'bdelete' : 'brestore'}`} 
          title={`${activeId === 2 ? 'Delete this record (Alt+L)' : 'Restore this record (Alt+R)'}`} 
          type='button' 
          accessKey={`${activeId === 2 ? 'l' : 'r'}`}
          onClick={handleDelete}
          >
        </button>
        :
        <button 
          id='btnDelete'
          className={`ibutton ${activeId === 2 || !notCommon ? 'bdelete disabled' : 'brestore disabled'}`} 
          title={`${activeId === 2 || !notCommon ? 'This record is protected or No privileges to delete this record' : 'This record is protected or No privileges to restore this record'}`} 
          disabled={true}
          >
        </button>
      )}

      <div className="form-head" dangerouslySetInnerHTML={{ __html: title }} />

      <div 
        className='form-desc' title='Form Name'>
        {userName ? userName : ''} 
        &nbsp; &nbsp; 
        <span className='copy' title='#Client Id'>{clientNumber ? `#${clientNumber}`: ''}</span> 
        &nbsp; &nbsp; 
        {formFullname.toUpperCase()}
        {activeId == Code.Option.Active.DELETE ? <a title={`This ${formFullname} is currently in the "DELETED" status`}>Status: DELETED</a> : ''}
      </div>

      {children}

    </div>
)};

interface SaveAdvanceProps {
  keyId: string;
  activeId: number | undefined;
  hasUnsaved?: boolean;
  title?: string;
  formName?: string;
  formId?: number | undefined;
  clientNumber?: string;
  userName?: string;
  Privilege: Privilege,
  showPrint?: boolean,
  handleDelete: () => void;
  className?: string;
  children?: React.ReactNode;
  companyId?: Number;
  disableSave?: boolean;
  disableReason?: string;
}

const SaveAdvance: React.FC<SaveAdvanceProps> = ({ 
  keyId, 
  activeId, 
  hasUnsaved = false,
  title='',
  formName='',
  formId = 0,
  clientNumber,
  userName,
  Privilege,
  showPrint = false,
  handleDelete,
  className='',
  disableSave = false,
  disableReason = '',
  children
}) => {
  const navigate = useNavigate();
  const formFullname = Utility.Get.FormFullName(formName);
  const isCommon = activeId === Code.Option.Active.COMMON ;
  const hasRights = Privilege.ALL ||  Privilege.CREATE || Privilege.MODIFY;
  const isNew = keyId === Code.Key.NEW;
  const isActive = activeId === Code.Option.Active.ACTIVE;

  let saveClass = 'tbutton bdisable';
  let saveTitle = isNew ? 'Create' : 'Update';
  let saveDesc = `${saveTitle} new record (Alt+S)`;
  let saveDisable = true;
  let actionName = isActive ? 'delete' : 'restore';
  

  if(disableSave){ saveDesc = disableReason; } 
  else if(isCommon){ saveDesc = `This record is protected or no privileges to ${saveTitle} this record.`; }
  else if(hasRights){
    if(hasUnsaved){ 
      saveClass = 'tbutton';
      saveDesc = `${saveTitle} this record (Alt+S)`;
      saveDisable = false;
    }
    else { saveDesc = "Nothing to save — no changes found on this form"; }
  }
  else {saveDesc = `No privileges to ${saveTitle} this record.`;}

  return (
    <div className={`form-buttons lcolor${formName} ${className}`}>
    {
      <button 
        id='btnSave' 
        className={saveClass} 
        type='submit' 
        accessKey='s'
        disabled={saveDisable}
        title={saveDesc}
        >
        {saveTitle}
      </button>
    }

    {!isNew && showPrint && (
      Privilege.ALL ||  Privilege.PRINT ?
      <button 
        id='btnPrint'
        className='ibutton bprint' 
        title={`Print this ${formFullname} (Alt+P)`} 
        type='button' 
        accessKey='p'
        onClick={() => navigate(`/${formName}/print/${keyId}`)}
        >
      </button>
      :
      <button 
        id='btnPrint'
        className='btnPrint bprint disabled'
        title={`No privileges to print this ${formFullname}`} 
        disabled={true}
        >
      </button>
    )}
      
    {!isNew && (
      !isCommon && !disableSave && (Privilege.ALL ||  Privilege.DELETE) ?
      <button 
        id='btnDelete'
        className={`ibutton ${isActive ? 'bdelete' : 'brestore'}`} 
        title={`${isActive ? 'Delete this record (Alt+L)' : 'Restore this record (Alt+R)'}`} 
        type='button' 
        accessKey={`${isActive ? 'l' : 'r'}`}
        onClick={handleDelete}
        >
      </button>
      :
      <button 
        id='btnDelete'
        className={`ibutton b${actionName} disabled`} 
        title={disableSave ? disableReason : `This record is protected or no privileges to ${actionName} this record`} 
        disabled={true}
        >
      </button>
    )}

    <div className="form-head" dangerouslySetInnerHTML={{ __html: title }} />

    <div 
      className='form-desc' title='Form Name'>
      {userName ? userName : ''} 
      &nbsp; &nbsp; 
      <span className='copy' title='#Client Id'>{clientNumber ? `#${clientNumber}`: ''}</span> 
      &nbsp; &nbsp; 
      {formFullname.toUpperCase()}
    </div>

    {children}

  </div>
)};

interface PrintButtonsProps {
  keyId: string;
  formName: string;
  userName?: string
  formTitle?: string,
  clientData?: Client | null,
  signData?: string,
  Privilege: Privilege,
  children?: React.ReactNode;
  fileName: string | undefined,
  printRef: RefObject<HTMLDivElement>,
  signRequire?: boolean,
  showModify?: boolean,
  docType?: number | undefined,
  landscape?: boolean,
  showFax?: boolean,
  showEmail?: boolean,
  showCall?: boolean;
  setMessage: Dispatch<SetStateAction<MessageType>>,
  setError: (error: string) => void
}

const PrintButtons: React.FC<PrintButtonsProps> = ({ 
  keyId, 
  formName,
  userName,
  formTitle,
  clientData,
  signData ='',
  Privilege,
  children,
  fileName = '',
  printRef,
  signRequire = false,
  showModify = true,
  docType = 2,
  landscape = false,
  showFax = true,
  showEmail = true,
  showCall = true,
  setMessage,
  setError
}) => {
  const navigate = useNavigate();
  // const [phoneNumber, setPhoneNumber] = useState<string>(clientData?.client_phone || clientData?.client_cell || clientData?.client_mobile || '');
  // const [faxNumber, setFaxNumber] = useState<string>(clientData?.client_fax || '');
  const [emailAddress, setEmailAddress] = useState<string>(clientData?.client_email || '');
  const [emailAddressCC, setEmailAddressCC] = useState<string>('');
  const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
  const [disableSendButton, setDisableSendButton] = useState<boolean>(false);
   
  
  var phoneNumber = clientData?.client_phone || clientData?.client_cell || clientData?.client_mobile || ''; 
  //var toEmail = clientData?.client_email || ''; 
  var faxNumber = clientData?.client_fax || ''; 

  if(!formTitle){
    formTitle = Utility.Convert.TitleCase(formName.replace("-"," "))  
  } 
  formName = formName.replace("-","")

  console.log("phoneNumber:",phoneNumber);
  console.log("faxNumber:",faxNumber);

  useEffect(() => {
    let hasEmail = false;
    if(emailAddress && Utility.isValid.Email(emailAddress)) {
      hasEmail = true;
    }
    if(emailAddressCC && Utility.isValid.Email(emailAddressCC)) {
      hasEmail = true;
    }
    setDisableSendButton(!hasEmail);
  }, [emailAddress, emailAddressCC]);

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>, to: boolean) => {
    to ? setEmailAddress(e.target.value) : setEmailAddressCC(e.target.value)
  };
  
  const onCallClicked = () => {
    if(!Utility.isValid.Phone(phoneNumber)) {
      Utility.Message.Info([`Phone number is invalid or not available`], setMessage);
      return;
    }
    window.location.href = `tel:+1${phoneNumber}`;  
  };

  const onEmailClicked = () => {
    if(!Utility.isValid.Email(emailAddress) && !Utility.isValid.Email(emailAddressCC)) {
      Utility.Message.Info([`Email id is invalid or not available`], setMessage);
      return;
    }

    const companyName = Utility.Storage.CompanyName();
    const titleData = `${Utility.Convert.TitleCase(formName)} Attached`;
    
    const contentData = Emailer.Basic.Attach({
      title: titleData,
      companyName: companyName,
      content: Emailer.Basic.Content(clientData?.client_first_name!,formName,companyName)});

    API.Data.sendAsEmail(printRef, emailAddress, emailAddressCC, titleData, contentData, landscape, setMessage, setError);

    setShowEmailInput(false);
  };



  const onFaxClicked = () => {
    if(!Utility.isValid.Phone(faxNumber)) {
      Utility.Message.Info([`Fax number is invalid or not available`], setMessage);
      return;
    }   

    const companyName = Utility.Storage.CompanyName();
    const titleData = `${Utility.Convert.TitleCase(formName)} Attached`;
    
    const contentData = Emailer.Basic.Attach({
      title: titleData,
      companyName: companyName,
      content: Emailer.Basic.Content(clientData?.client_first_name!,formName,companyName)});

    API.Data.sendAsEmail(printRef, `1${faxNumber}@srfax.com`, "", titleData, contentData, landscape, setMessage, setError);
  };



  const onSaveDocument = () => {
    //console.log("signData.length==========>",signData.length);
    if(signRequire && signData.length<10) {
      Utility.Message.Error([`Client Signature required!`], setMessage);
      return;
    }   
    API.Data.saveToDocument(printRef, Code.Form.Id.CLIENT, clientData?.client_id, docType, landscape, setMessage, setError);
  };
  
  return (
    <div className="print-buttons">
      
      {showModify && keyId !== Code.Key.NEW && (
        Privilege.ALL ||  Privilege.MODIFY ?
        <button 
          id="btnModify"
          className="tbutton" 
          title={`Modify this ${formTitle} (Alt+S)`}
          type="button" 
          accessKey="m"
          onClick={() => navigate(`/${formName}/${keyId}`)}
          >Modify
        </button>
        :
        <button 
          id="btnModify"
          className={`tbutton disabled`} 
          title={`No privileges to modify this ${formTitle}`} 
          disabled={true}
          >
        </button>
      )}

      {
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnPrint" 
          className="ibutton bprint" 
          accessKey="p"
          title="Print Preview (Alt+P)"
          onClick={window.print}
        ></button>
        :
        <button 
          id="btnPrint" 
          className="ibutton bprint disabled" 
          title={`No privileges to print this ${formTitle}.`}
          disabled={true}
          ></button>
      }

      {
        ((showEmail && emailAddress && emailAddress.length>0) || (emailAddress && emailAddress.length>0)) && (Privilege.ALL ||  Privilege.PRINT ?
        <>
          <button 
            id="btnEmailView" 
            className="ibutton bemail" 
            accessKey="e"
            title="Email (Alt+E)"
            onClick={() => setShowEmailInput(!showEmailInput)}
          ></button>
          {showEmailInput && (
            <div className='button-popup'>
              <Field.Text id={"mailto"} value={emailAddress} title={"To"} onChange={(e) => onEmailChange(e, true)} horizontal={true} width={90}  />
              <Field.Text id={"mailcc"} value={emailAddressCC} title={"Cc"} onChange={(e) => onEmailChange(e, false)} horizontal={true} width={90}  />
              <button 
                id="btnEmail" 
                className={`tbutton fright ${disableSendButton ? 'disabled' : ''}`} 
                title="Click to send email"
                disabled={disableSendButton}
                onClick={onEmailClicked}
              >Send</button>
              <button 
                id="btnEmail" 
                className={`tbutton fright mr2 bcolor10`} 
                title="Cancel/Close"
                onClick={() => setShowEmailInput(false)}
              >Cancel</button>
            </div>
          )}
        </>
        :
        <button 
          id="btnEmailView" 
          className="ibutton bemail disabled" 
          title={`No privileges to email this ${formTitle}.`}
          disabled={false}
          ></button>) 
      }

      {
        showFax && faxNumber && faxNumber.length==10 && (Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnFax" 
          className="ibutton bfax" 
          accessKey="f"
          title="Fax (Alt+F)"
          onClick={onFaxClicked}
        ></button>
        :
        <button 
          id="btnFax" 
          className="ibutton bfax disabled" 
          title={`No privileges to fax this ${formTitle}.`}
          disabled={false}
          ></button>) 
      }

      {
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnSaveAsPdf" 
          className="ibutton bsavedoc" 
          title="Save to Document"
          onClick={onSaveDocument}
        ></button>
        :
        <button 
          id="btnPdf" 
          className="ibutton bsace disabled" 
          title={`No privileges to save this ${formTitle} as pdf.`}
          disabled={true}
          ></button>
      }

      {
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnExportPdf" 
          className="ibutton bexport" 
          title="Download"
          onClick={() => downloadPdf(`${formName}-${fileName}`, printRef, setMessage)}
        ></button>
        :
        <button 
          id="btnPdf" 
          className="ibutton bexport disabled" 
          title={`No privileges to download this ${formTitle} as pdf.`}
          disabled={true}
          ></button>
      }

      {
        showCall && phoneNumber && phoneNumber.length==10 && (Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnCall" 
          className="ibutton bcall" 
          accessKey="p"
          title="Call (Alt+C)"
          onClick={onCallClicked}
        ></button>
        :
        <button 
          id="btnCall" 
          className="ibutton bcall disabled" 
          title={`No privileges to call this client.`}
          disabled={false}
          ></button>)
      }

      <div 
        className="print-head">
        {userName ? userName : ""} 
        &nbsp; 
        <span className='copy'>{clientData?.client_number ? `#${clientData?.client_number}`: ''}</span> 
        &nbsp; 
        {formTitle.toUpperCase()}
      </div>

      {children}

    </div>
)};

interface PrintDocumentProps {
  keyId: string;
  fileUrl?: string | null;
  formName: string;
  userName?: string
  formTitle?: string,
  clientData?: Client | null,
  signData?: string,
  Privilege: Privilege,
  children?: React.ReactNode;
  fileName: string | undefined,
  printRef: RefObject<HTMLDivElement>,
  signRequire?: boolean,
  showModify?: boolean,
  docType?: number | undefined,
  landscape?: boolean,
  showFax?: boolean,
  showEmail?: boolean,
  showCall?: boolean;
  setMessage: Dispatch<SetStateAction<MessageType>>,
  setError: (error: string) => void
}

const PrintDocument: React.FC<PrintDocumentProps> = ({ 
  keyId, 
  fileUrl,
  formName,
  userName,
  formTitle,
  clientData,
  signData ='',
  Privilege,
  children,
  fileName = '',
  printRef,
  signRequire = false,
  showModify = true,
  docType = 2,
  landscape = false,
  showFax = true,
  showEmail = true,
  showCall = true,
  setMessage,
  setError
}) => {
  const navigate = useNavigate();
  if(!formTitle){
    formTitle = Utility.Convert.TitleCase(formName.replace("-"," "))  
  } 
  formName = formName.replace("-","")
  const phoneNumber = clientData?.client_phone || clientData?.client_cell || clientData?.client_mobile || '';
  const faxNumber = clientData?.client_fax || '';
  const emailAddress = clientData?.client_email || '';

  const onCallClicked = () => {
    if(!Utility.isValid.Phone(phoneNumber)) {
      Utility.Message.Info([`Phone number is invalid or not available`], setMessage);
      return;
    }
    window.location.href = `tel:+1${phoneNumber}`;  
  };

  const onEmailClicked = () => {
    if(!Utility.isValid.Email(emailAddress)) {
      Utility.Message.Info([`Email id is invalid or not available`], setMessage);
      return;
    }

    const companyName = Utility.Storage.CompanyName();
    const titleData = `${Utility.Convert.TitleCase(formName)} Attached`;
    
    const contentData = Emailer.Basic.Attach({
      title: titleData,
      companyName: companyName,
      content: Emailer.Basic.Content(clientData?.client_first_name!,formName,companyName)});

    API.Data.sendAsEmail(printRef, emailAddress, "", titleData, contentData, landscape, setMessage, setError);
  };



  const onFaxClicked = () => {
    if(!Utility.isValid.Phone(faxNumber)) {
      Utility.Message.Info([`Fax number is invalid or not available`], setMessage);
      return;
    }   

    const companyName = Utility.Storage.CompanyName();
    const titleData = `${Utility.Convert.TitleCase(formName)} Attached`;
    
    const contentData = Emailer.Basic.Attach({
      title: titleData,
      companyName: companyName,
      content: Emailer.Basic.Content(clientData?.client_first_name!,formName,companyName)});

    API.Data.sendAsEmail(printRef, `1${faxNumber}@srfax.com`, "", titleData, contentData, landscape, setMessage, setError);
  };



  const onSaveDocument = () => {
    //console.log("signData.length==========>",signData.length);
    if(signRequire && signData.length<10) {
      Utility.Message.Error([`Client Signature required!`], setMessage);
      return;
    }   
    API.Data.saveToDocument(printRef, Code.Form.Id.CLIENT, clientData?.client_id, docType, landscape, setMessage, setError);
  };
  
  return (
    <div className="print-buttons">
      
      {showModify && keyId !== Code.Key.NEW && (
        Privilege.ALL ||  Privilege.MODIFY ?
        <button 
          id="btnModify"
          className="tbutton" 
          title={`Modify this ${formTitle} (Alt+S)`}
          type="button" 
          accessKey="m"
          onClick={() => navigate(`/${formName}/${keyId}`)}
          >Modify
        </button>
        :
        <button 
          id="btnModify"
          className={`tbutton disabled`} 
          title={`No privileges to modify this ${formTitle}`} 
          disabled={true}
          >
        </button>
      )}

      {
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnPrint" 
          className="ibutton bprint" 
          accessKey="p"
          title="Print Preview (Alt+P)"
          onClick={() => {
            const iframe = document.querySelector('.preview-file') as HTMLIFrameElement;
            if (iframe?.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            } else if (fileUrl) {
              const printWindow = window.open(fileUrl, '_blank');
              printWindow?.addEventListener('load', () => printWindow.print());
            }
          }}
        ></button>
        :
        <button 
          id="btnPrint" 
          className="ibutton bprint disabled" 
          title={`No privileges to print this ${formTitle}.`}
          disabled={true}
          ></button>
      }

      {
        fileUrl && showEmail && (Privilege.ALL ||  Privilege.VIEW ?
        <button
          id="btnOpen"
          className="ibutton bopen"
          accessKey="o"
          title="Open in Browser (Alt+O)"
          onClick={() => window.open(fileUrl, '_blank')}
        ></button>
        :
        <button 
          id="btnOpen" 
          className="ibutton bopen disabled" 
          title={`No privileges to open this ${formTitle}.`}
          disabled={false}
          ></button>) 
      }

      {/* {
        showEmail && emailAddress && emailAddress.length>0 && (Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnEmail" 
          className="ibutton bemail" 
          accessKey="e"
          title="Email (Alt+E)"
          onClick={onEmailClicked}
        ></button>
        :
        <button 
          id="btnEmail" 
          className="ibutton bemail disabled" 
          title={`No privileges to email this ${formTitle}.`}
          disabled={false}
          ></button>) 
      } */}

      {
        fileUrl && (Privilege.ALL ||  Privilege.PRINT) ?
        <button
          id="btnDownload"
          className="ibutton bexport"
          accessKey="d"
          title="Download (Alt+D)"
          onClick={() => {
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = `document_${new Date().toISOString().replace(/[-T:Z.]/g, '').slice(0, 14)}.pdf`;
            a.click();
          }}
        ></button>
        :
        <button 
          id="btnPdf" 
          className="ibutton bexport disabled" 
          title={`No privileges to download this ${formTitle} as pdf.`}
          disabled={true}
          ></button>
      }


      <div 
        className="print-head">
        {userName ? userName : ""} 
        &nbsp; 
        <span className='copy'>{clientData?.client_number ? `#${clientData?.client_number}`: ''}</span> 
        &nbsp; 
        {formTitle.toUpperCase()}
      </div>

      {children}

    </div>
)};


export const Button = {
  Action: ActionButton,
  Disable: ActionDisable, 
  Icon: IconButton,
  FrameAction: FrameActionButton,
  AutoGenerateButton: AutoGenerateButton,
  Link: LinkButton,
  Create: CreateButton,
  Delete: DeleteButton,
  Top: TopButtons,
  Save: SaveButtons,
  SaveAdvance: SaveAdvance,
  Print: PrintButtons,
  PrintDocument: PrintDocument,
}